import { ApiError, path } from 'utils'
import { z } from 'zod'

import { PlatformWidePermission } from '../auth-claims/permissions'
import { boundarySchema, rxSchema } from '../geojson-schemas'
import { defineApi } from '../http'
import {
  baseCffSchema,
  boundaryPlanAssetSchema,
  crusherRefusalSchema,
  GeoSpatialFileType,
  gen3FileSchema,
  rxPlanAssetSchema,
  shapeFileSchema,
  zipFileSchema
} from '../locutus'
import { guidancePrimitiveUnion } from '../locutus/guidance'
import { MeasureSystem } from '../shared'
import { controllableApplicationTypes } from '../stitch'

const crusherResponseSchema = z.object({
  files: z.array(
    z.object({
      /**
       * Presigned url to the converted file in S3, valid for a limited time (e.g., 15 minutes) for secure download.
       */
      url: z.url(),
      /**
       * User-supplied file name pre conversion.
       */
      name: z.string(),
      /**
       * Size of the converted file in bytes.
       */
      size: z.number(),
      /**
       * The client, farm, and field the asset was exported under (if any).
       */
      cff: baseCffSchema.nullable()
    })
  )
})

export const crusherConvertBoundaryToGeojsonApi = defineApi({
  pathSchema: z.object({
    from: z.string()
  }),
  pathFor: (p) =>
    path(`/plan/convert/${p.from}/geojson`, {
      type: 'boundary'
    }),
  name: 'ConvertBoundaryFileToGeoJSON',
  description:
    'Convert uploaded boundary file (shapefile or Gen3) to GeoJSON format for field boundary visualization',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  method: 'POST',
  requestBodySchema: shapeFileSchema.or(zipFileSchema).or(gen3FileSchema),
  requestBodyEncoding: 'form-data',
  responseSchema: crusherResponseSchema
})

export const crusherConvertRxToGeojsonApi = defineApi({
  pathSchema: z.object({
    from: z.enum(GeoSpatialFileType)
  }),
  querySchema: z.object({
    measureSystem: z.enum(MeasureSystem),
    type: controllableApplicationTypes
  }),
  pathFor: (p) =>
    path(`/plan/convert/${p.from}/geojson`, {
      measureSystem: p.measureSystem,
      type: p.type
    }),
  name: 'ConvertPrescriptionFileToGeoJSON',
  description:
    'Convert uploaded prescription file to GeoJSON format with specified unit system for interpreting application rate attributes',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  method: 'POST',
  requestBodySchema: shapeFileSchema.or(zipFileSchema).or(gen3FileSchema),
  requestBodyEncoding: 'form-data',
  responseSchema: crusherResponseSchema
})

/**
 * A conversion route reachable from a browser.
 */
const jwtConvertPath = (from: string, to: string, query: Parameters<typeof path>[1]) =>
  path(`/plan/jwt/convert/${from}/${to}`, query)

/**
 * The shapefile parts of an AgGPS guidance bundle, plus its `.pos` file. A
 * `.pos` body is empty — the filename is what carries the field origin.
 */
const guidanceShapeFileSchema = z.object({
  ...shapeFileSchema.shape,
  pos: z
    .instanceof(File)
    .refine((file) => file.name.toLowerCase().endsWith('.pos'), {
      message: 'File must be a .pos file'
    })
    .optional()
})

export const crusherConvertGuidanceToGeojsonApi = defineApi({
  pathFor: () => jwtConvertPath('shapefile', 'geojson', { type: 'guidance' }),
  name: 'ConvertGuidanceFileToGeoJSON',
  description:
    'Convert an uploaded guidance swath shapefile (the Swaths layer of an AgGPS field bundle) into one GeoJSON guidance primitive per line. Each line comes back as a presigned download URL holding a single named AB line or curve. Upload the whole bundle as a zip, or the shapefile parts alongside the bundle .pos file, whose name carries the field origin. A bundle containing pivot swaths is rejected in whole.',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  method: 'POST',
  requestBodySchema: guidanceShapeFileSchema.or(zipFileSchema),
  requestBodyEncoding: 'form-data',
  responseSchema: crusherResponseSchema
})

/**
 * Why crusher could not read a plan file.
 */
export enum PlanFileReadError {
  /**
   * A .2020 that holds no plans, such as an account export.
   */
  NoAssets = 'noAssets',
  /**
   * The file parsed, but its shapes could not be placed on the map.
   */
  NoGeometry = 'noGeometry',
  /**
   * The file is corrupt or incomplete.
   */
  Damaged = 'damaged',
  UnsupportedFormat = 'unsupportedFormat',
  Unknown = 'unknown'
}

/**
 * Matched on fragments of crusher's refusal message.
 */
const READ_ERROR_BY_REASON: readonly (readonly [string, PlanFileReadError])[] = [
  ['no plan data present', PlanFileReadError.NoAssets],
  ['no usable geometry', PlanFileReadError.NoGeometry],
  ['interior-only polygons', PlanFileReadError.NoGeometry],
  ['not valid', PlanFileReadError.Damaged],
  ['no field data', PlanFileReadError.Damaged],
  ['unable to open', PlanFileReadError.Damaged],
  ['unexpected file type', PlanFileReadError.UnsupportedFormat],
  ['unsupported input format', PlanFileReadError.UnsupportedFormat]
]

export const mapPlanFileReadError = (error: unknown): PlanFileReadError => {
  const refusal =
    error instanceof ApiError ? crusherRefusalSchema.safeParse(error.responseText) : undefined
  const reason = refusal?.success === true ? (refusal.data.message ?? '').toLowerCase() : ''

  return (
    READ_ERROR_BY_REASON.find(([fragment]) => reason.includes(fragment))?.[1] ??
    PlanFileReadError.Unknown
  )
}

/**
 * Converts an upload into one GeoJSON plan per asset without storing anything,
 * so a client can review a file before importing it. A .2020 returns every
 * asset it holds regardless of `type`; a shapefile is read as the `type` and
 * `measureSystem` the caller declares.
 */
export const inspectPlanFileApi = defineApi({
  pathSchema: z.object({
    from: z.enum(GeoSpatialFileType)
  }),
  querySchema: z.object({
    type: controllableApplicationTypes.or(z.literal('boundary')),
    /**
     * The units a shapefile's rate column is in.
     */
    measureSystem: z.enum(MeasureSystem)
  }),
  pathFor: (p) =>
    jwtConvertPath(p.from, 'geojson', { type: p.type, measureSystem: p.measureSystem }),
  name: 'InspectPlanFile',
  description:
    'Read an uploaded .2020, shapefile, or zipped shapefile and return every field boundary and prescription it contains as GeoJSON, without storing anything. Use this to show a grower what a file holds before importing it. A .2020 returns each of its assets with the application type and the client/farm/field recorded in the file; a shapefile is read as whichever single kind the `type` parameter names.',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  method: 'POST',
  requestBodySchema: shapeFileSchema.or(zipFileSchema).or(gen3FileSchema),
  requestBodyEncoding: 'form-data',
  responseSchema: crusherResponseSchema,
  errorMap: mapPlanFileReadError
})

export const crusherS3BoundaryGeojsonSchema = z.object({
  data: boundarySchema,
  type: z.literal('boundary')
})

export const crusherS3RxGeojsonSchema = z.object({
  data: rxSchema,
  type: z.literal('rx')
})

/**
 * One converted plan as it lands in S3. Discriminated so that whoever reads a
 * whole upload can sort boundaries from prescriptions without prior knowledge.
 */
export const crusherS3PlanSchema = z.discriminatedUnion('type', [
  crusherS3BoundaryGeojsonSchema,
  crusherS3RxGeojsonSchema
])

export type CrusherS3Plan = z.infer<typeof crusherS3PlanSchema>

/**
 * A converted line, minus the implement properties a stored primitive carries:
 * guidance files record no swath width, row spacing, or offset, so whoever
 * persists these supplies them — see DEFAULT_GUIDANCE_PROPERTIES.
 */
export const crusherS3GuidanceGeojsonSchema = z.object({
  data: z.intersection(
    guidancePrimitiveUnion({}),
    z.object({
      /**
       * The line's name, derived from the source file where possible.
       */
      name: z.string().optional(),
      /**
       * Field origin in WGS84, not directly related to the line.
       */
      origin: z
        .object({
          lat: z.number().min(-90).max(90),
          lon: z.number().min(-180).max(180)
        })
        .optional()
    })
  ),
  name: z.string(),
  type: z.literal('guidancePrimitive')
})

export const crusherConvertAssetToFileApi = defineApi({
  pathSchema: z.object({
    from: z.enum(GeoSpatialFileType),
    to: z.enum([GeoSpatialFileType.ShapeFile, GeoSpatialFileType.Gen3_2020])
  }),
  querySchema: z.object({
    type: controllableApplicationTypes.or(z.literal('boundary')),
    measureSystem: z.enum(MeasureSystem).optional()
  }),
  pathFor: (p) =>
    path(`/plan/convert/${p.from}/${p.to}`, {
      type: p.type,
      measureSystem: p.type === 'boundary' ? undefined : 'imperial'
    }),
  name: 'ConvertPlanningAssetToFile',
  description:
    'Convert planning asset (boundary or prescription file) from one geospatial format to another (shapefile or Gen3)',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  method: 'POST',
  requestBodySchema: z.array(rxPlanAssetSchema.or(boundaryPlanAssetSchema)),
  responseSchema: crusherResponseSchema
})

export const crusherConvertBoundaryToFileApi = defineApi({
  pathSchema: z.object({
    from: z.enum(GeoSpatialFileType),
    to: z.enum([GeoSpatialFileType.ShapeFile, GeoSpatialFileType.Gen3_2020])
  }),
  querySchema: z.object({
    type: z.literal('boundary')
  }),
  pathFor: (p) =>
    path(`/plan/convert/${p.from}/${p.to}`, {
      type: 'boundary'
    }),
  name: 'ConvertBoundaryToFile',
  description:
    'Convert boundary data from one geospatial file format to another (shapefile or Gen3)',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  method: 'POST',
  responseSchema: crusherResponseSchema
})

export const crusherConvertRxToFileApi = defineApi({
  pathSchema: z.object({
    from: z.enum(GeoSpatialFileType),
    to: z.enum([GeoSpatialFileType.ShapeFile, GeoSpatialFileType.Gen3_2020])
  }),
  querySchema: z.object({
    measureSystem: z.enum(MeasureSystem),
    type: controllableApplicationTypes
  }),
  pathFor: (p) =>
    path(`/plan/convert/${p.from}/${p.to}`, {
      measureSystem: p.measureSystem,
      type: p.type
    }),
  name: 'ConvertPrescriptionToFile',
  description:
    'Convert prescription (Rx) data from one geospatial file format to another with specified unit system',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  method: 'POST',
  responseSchema: crusherResponseSchema
})
